import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Search, User, UserPlus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { validateIndianMobile } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * A modal that asks the agent to pick an existing Client or Lead before
 * launching a product-specific form. Under the Leads tab, agents can also
 * create a brand-new lead inline (name + phone) and immediately continue —
 * no need to leave and reopen this flow.
 */
const ClientLeadPickerDialog = ({ open, onClose, clients = [], leads = [], onPick, title = 'Select client or lead', description, onLeadCreated }) => {
  const [tab, setTab] = useState('clients');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [showNewLead, setShowNewLead] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', phone: '', source: 'Quick Add' });
  const [saving, setSaving] = useState(false);
  // Post-save prompt state: after a quick lead is created, ask if the agent
  // wants to enrich it now with product/priority/etc, or continue straight
  // to the product form with just name+mobile captured.
  const [enrichPrompt, setEnrichPrompt] = useState(null); // { lead, onFinish }
  const [enrichForm, setEnrichForm] = useState({ product: '', temperature: 'Warm', expected_premium: '', notes: '', follow_up_date: '' });
  const [enrichSaving, setEnrichSaving] = useState(false);
  const [enrichMode, setEnrichMode] = useState(false); // false = prompt, true = inline form

  const sourceList = tab === 'clients' ? clients : leads;
  const filtered = sourceList.filter((item) => {
    const label = (item.name || '').toLowerCase();
    const phone = tab === 'clients' ? (item.primary_mobile || '') : (item.phone || '');
    return !query || label.includes(query.toLowerCase()) || phone.includes(query);
  }).slice(0, 40);

  const pick = () => {
    if (!selectedId) return;
    const picked = sourceList.find((i) => (tab === 'clients' ? i.client_id : i.lead_id) === selectedId);
    if (!picked) return;
    onPick({ type: tab === 'clients' ? 'client' : 'lead', id: selectedId, client_id: tab === 'clients' ? selectedId : null, name: picked.name });
    setQuery(''); setSelectedId(''); setShowNewLead(false); setNewLead({ name: '', phone: '', source: 'Quick Add' });
  };

  const createLeadInline = async () => {
    const mv = validateIndianMobile(newLead.phone);
    if (!newLead.name.trim()) { toast.error('Name is required'); return; }
    if (!mv.ok) { toast.error(mv.error); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: newLead.name.trim(), phone: mv.digits, source: newLead.source || 'Quick Add', status: 'New', temperature: 'Warm',
      }, { withCredentials: true });
      const lead_id = data.lead_id || data.id;
      if (onLeadCreated) onLeadCreated(data);
      // Show the enrichment prompt — do NOT auto-navigate. The agent picks:
      // "Add more details now" (inline enrich form) or "Skip — continue" (jump straight
      // to product form with just name+mobile captured).
      setEnrichPrompt({ lead: { lead_id, name: newLead.name.trim(), phone: mv.digits } });
      setEnrichMode(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally { setSaving(false); }
  };

  const skipEnrichAndContinue = () => {
    const l = enrichPrompt?.lead;
    if (!l) return;
    toast.success('Lead saved with basic details');
    onPick({ type: 'lead', id: l.lead_id, client_id: null, name: l.name });
    setShowNewLead(false); setNewLead({ name: '', phone: '', source: 'Quick Add' });
    setEnrichPrompt(null); setEnrichMode(false);
    setEnrichForm({ product: '', temperature: 'Warm', expected_premium: '', notes: '', follow_up_date: '' });
  };

  const saveEnrichment = async () => {
    const l = enrichPrompt?.lead;
    if (!l) return;
    setEnrichSaving(true);
    try {
      await axios.put(`${API}/leads/${l.lead_id}`, {
        product: enrichForm.product || null,
        temperature: enrichForm.temperature || 'Warm',
        expected_premium: enrichForm.expected_premium === '' ? null : parseFloat(enrichForm.expected_premium),
        notes: enrichForm.notes || null,
        follow_up_date: enrichForm.follow_up_date || null,
      }, { withCredentials: true });
      toast.success('Lead saved with full details');
      onPick({ type: 'lead', id: l.lead_id, client_id: null, name: l.name });
      setShowNewLead(false); setNewLead({ name: '', phone: '', source: 'Quick Add' });
      setEnrichPrompt(null); setEnrichMode(false);
      setEnrichForm({ product: '', temperature: 'Warm', expected_premium: '', notes: '', follow_up_date: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save details');
    } finally { setEnrichSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setQuery(''); setSelectedId(''); setShowNewLead(false); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="client-lead-picker">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description || 'Every product must be linked to an existing client or lead.'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" role="tablist">
            <button type="button" onClick={() => { setTab('clients'); setSelectedId(''); setShowNewLead(false); }}
              className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${tab === 'clients' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600'}`} data-testid="picker-tab-clients">
              <User className="h-3.5 w-3.5" /> Clients ({clients.length})
            </button>
            <button type="button" onClick={() => { setTab('leads'); setSelectedId(''); }}
              className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${tab === 'leads' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600'}`} data-testid="picker-tab-leads">
              <UserPlus className="h-3.5 w-3.5" /> Leads ({leads.length})
            </button>
          </div>

          {tab === 'leads' && !showNewLead && (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowNewLead(true)} className="w-full border-dashed border-blue-300 text-blue-800 hover:bg-blue-50" data-testid="picker-add-new-lead-button">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add New Lead
            </Button>
          )}

          {tab === 'leads' && showNewLead && !enrichPrompt && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="picker-new-lead-form">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Name *</Label>
                  <Input value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} data-testid="picker-new-lead-name" />
                </div>
                <div><Label className="text-xs">Phone (10-digit) *</Label>
                  <Input value={newLead.phone} maxLength={10} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value.replace(/\D/g, '') })} data-testid="picker-new-lead-phone" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewLead(false)}>Cancel</Button>
                <Button type="button" size="sm" onClick={createLeadInline} disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="picker-new-lead-save">
                  {saving ? 'Creating…' : 'Save & Next'}
                </Button>
              </div>
            </div>
          )}

          {enrichPrompt && !enrichMode && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-3" data-testid="picker-enrich-prompt">
              <div>
                <p className="text-sm font-semibold text-emerald-900">Lead "{enrichPrompt.lead.name}" saved ✓</p>
                <p className="text-xs text-emerald-800 mt-1">Add more details now — product interest, priority, follow-up date — so this lead is complete in one step?</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline" onClick={skipEnrichAndContinue} data-testid="picker-enrich-skip">
                  No, continue with basic details
                </Button>
                <Button type="button" size="sm" onClick={() => setEnrichMode(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="picker-enrich-open">
                  Yes, add more details
                </Button>
              </div>
            </div>
          )}

          {enrichPrompt && enrichMode && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="picker-enrich-form">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Product interest</Label>
                  <Input placeholder="Health, Term, Motor…" value={enrichForm.product} onChange={(e) => setEnrichForm({ ...enrichForm, product: e.target.value })} data-testid="picker-enrich-product" />
                </div>
                <div><Label className="text-xs">Expected Premium (₹)</Label>
                  <Input type="number" min="0" placeholder="Leave blank if unknown" value={enrichForm.expected_premium} onChange={(e) => setEnrichForm({ ...enrichForm, expected_premium: e.target.value })} data-testid="picker-enrich-premium" />
                </div>
                <div><Label className="text-xs">Priority</Label>
                  <select className="w-full h-9 border border-slate-200 rounded px-2 text-sm bg-white" value={enrichForm.temperature} onChange={(e) => setEnrichForm({ ...enrichForm, temperature: e.target.value })} data-testid="picker-enrich-priority">
                    <option value="Hot">Hot</option>
                    <option value="Warm">Warm</option>
                    <option value="Cold">Cold</option>
                  </select>
                </div>
                <div><Label className="text-xs">Follow-up date</Label>
                  <Input type="date" value={enrichForm.follow_up_date} onChange={(e) => setEnrichForm({ ...enrichForm, follow_up_date: e.target.value })} data-testid="picker-enrich-follow-up" />
                </div>
              </div>
              <div><Label className="text-xs">Notes</Label>
                <Input placeholder="Anything worth remembering" value={enrichForm.notes} onChange={(e) => setEnrichForm({ ...enrichForm, notes: e.target.value })} data-testid="picker-enrich-notes" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline" onClick={skipEnrichAndContinue} data-testid="picker-enrich-form-skip">
                  Skip
                </Button>
                <Button type="button" size="sm" onClick={saveEnrichment} disabled={enrichSaving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="picker-enrich-save">
                  {enrichSaving ? 'Saving…' : 'Save details & continue'}
                </Button>
              </div>
            </div>
          )}

          {!showNewLead && !enrichPrompt && (<>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2 top-2.5" />
            <Input className="pl-8" placeholder="Search by name or mobile" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="picker-search-input" />
          </div>
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
            {filtered.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No matches.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((item) => {
                  const id = tab === 'clients' ? item.client_id : item.lead_id;
                  const phone = tab === 'clients' ? item.primary_mobile : item.phone;
                  return (
                    <li key={id}><button type="button" onClick={() => setSelectedId(id)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 ${selectedId === id ? 'bg-blue-50 border-l-4 border-blue-700' : ''}`} data-testid={`picker-row-${id}`}>
                        <span className="font-medium text-slate-900">{item.name}</span>
                        <span className="text-xs text-slate-500">{phone || '—'}</span>
                      </button></li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" disabled={!selectedId} onClick={pick} data-testid="picker-confirm-button">Continue</Button>
          </div>
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientLeadPickerDialog;
